import { Clock01Icon, File01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useReportHistory } from '@/hooks/use-reports';

export const Route = createFileRoute('/_authenticated/reports/history')({
  component: ReportHistoryPage,
});

function ReportHistoryPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useReportHistory(50, 0);

  const history = data?.data ?? [];
  const total = data?.total ?? 0;

  const getFormatBadge = (format: string) => {
    const colors: Record<string, string> = {
      pdf: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      xlsx: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      csv: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return colors[format] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <HugeiconsIcon icon={Clock01Icon} className="size-6" />
          {t('sidebar.reports.history', 'Report History')}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t('reports.history.description', 'Previously generated and downloaded reports')}
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading…</div>
      ) : history.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <HugeiconsIcon
              icon={File01Icon}
              className="size-12 mx-auto text-muted-foreground/30 mb-4"
            />
            <p className="text-muted-foreground">No report history yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Reports will appear here after they are generated or downloaded.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{total} report(s) generated</p>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report Name</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Dashboard</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Generated By</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">{entry.report_name}</TableCell>
                      <TableCell>
                        <Badge className={getFormatBadge(entry.format)}>
                          {entry.format.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.dashboard_id || '—'}
                      </TableCell>
                      <TableCell>
                        {entry.scheduled_report_id ? (
                          <Badge variant="outline">Scheduled</Badge>
                        ) : (
                          <Badge variant="secondary">Manual</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.generated_by ? `${entry.generated_by.slice(0, 8)}…` : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
